import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Tabs, message } from 'antd';
import { useHookstate } from '@hookstate/core';
import {
  JobHeader,
  MediaGallery,
  ImageComparer,
  MetadataPanel,
  JsonView,
  FullScreenViewer
} from './components';
import { extractMediaFromJob, extractPromptsFromJob, downloadMedia, copyToClipboard } from './utils';
import './JobModal.css';

const JobModal = ({ visible, data, onClose }) => {
  const [activeTab, setActiveTab] = useState('gallery');
  const [fullScreenImage, setFullScreenImage] = useState(null);
  
  // State for image selection
  const compareMode = useHookstate(false);
  const selectedInputs = useHookstate([]);
  const selectedOutputs = useHookstate([]);
  
  // Extract media and prompts from job data
  const { inputs, outputs } = useMemo(() => {
    return extractMediaFromJob(data);
  }, [data]);

  // Combine all media items for FullScreenViewer
  const allMediaItems = useMemo(() => {
    return [...inputs, ...outputs];
  }, [inputs, outputs]);

  const prompts = useMemo(() => {
    return extractPromptsFromJob(data);
  }, [data]);
  
  // Reset selections when modal opens/closes
  useEffect(() => {
    if (visible) {
      compareMode.set(false);
      selectedInputs.set([]);
      selectedOutputs.set([]);
      setActiveTab('gallery');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  
  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && fullScreenImage) {
        setFullScreenImage(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, fullScreenImage]);
  
  if (!data) return null;
  
  const handleDownloadAll = async () => {
    // Only download outputs, not inputs
    const results = await downloadMedia(outputs);
    if (results.success > 0) {
      message.success(`Downloaded ${results.success} files`);
    }
    if (results.failed > 0) {
      message.warning(`Failed to download ${results.failed} files`);
    }
  };
  
  const handleCopyUrls = () => {
    const urls = [...inputs, ...outputs]
      .map(m => m.url)
      .filter(Boolean);
    if (urls.length > 0) {
      copyToClipboard(urls.join('\n'));
      message.success('URLs copied to clipboard');
    }
  };
  
  const tabs = [
    {
      key: 'gallery',
      label: '🎨 Gallery',
      children: (
        <div className="job-modal-gallery-tab">
          <MediaGallery
            inputs={inputs}
            outputs={outputs}
            prompts={prompts}
            compareMode={compareMode.value}
            selectedInputs={selectedInputs}
            selectedOutputs={selectedOutputs}
            onImageClick={setFullScreenImage}
            onToggleCompare={() => compareMode.set(!compareMode.value)}
          />
        </div>
      )
    },
    {
      key: 'compare',
      label: '🔄 Compare',
      disabled: inputs.length === 0 || outputs.length === 0,
      children: (
        <div className="job-modal-compare-tab">
          <ImageComparer
            inputs={inputs}
            outputs={outputs}
            selectedInputs={selectedInputs.value}
            selectedOutputs={selectedOutputs.value}
            onFullScreen={setFullScreenImage}
          />
        </div>
      )
    },
    {
      key: 'metadata',
      label: '📊 Details',
      children: (
        <div className="job-modal-metadata-tab">
          <MetadataPanel data={data} />
        </div>
      )
    },
    {
      key: 'json',
      label: '{ } JSON',
      children: (
        <div className="job-modal-json-tab">
          <JsonView data={data} />
        </div>
      )
    }
  ];
  
  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width="95%"
        style={{ maxWidth: 1400, top: 20 }}
        className="job-modal"
        destroyOnClose
      >
        <div className="job-modal-container">
          <JobHeader
            data={data}
            onDownloadAll={handleDownloadAll}
            onCopyUrls={handleCopyUrls}
          />
          
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabs}
            className="job-modal-tabs"
          />
        </div>
      </Modal>
      
      {fullScreenImage && (
        <FullScreenViewer
          image={fullScreenImage}
          images={allMediaItems}
          onClose={() => setFullScreenImage(null)}
        />
      )}
    </>
  );
};

export default JobModal;
